import { ChatThread } from "@/schema"
import { useQuery, UseQueryOptions } from "@tanstack/react-query"

import { useApiContext } from "@/hooks/api/use-api-context"
import { useBookId } from "@/hooks/use-book-id"
import { useDevAuth } from "@/hooks/use-dev-auth"
import { useProductChatAccess } from "@/hooks/use-product-chat-access"

import { ChatThreadParams, fetchChatThread, fetchChatThreads } from "./api"

export function useFetchChatThread(
  threadId: string,
  options?: Omit<UseQueryOptions<ChatThread, Error>, "queryKey" | "queryFn">
) {
  const { enabled: chatEnabled } = useProductChatAccess()
  const getContext = useApiContext()
  return useQuery<ChatThread, Error>({
    queryKey: ["chat_thread", threadId],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchChatThread(context, threadId)
    },
    ...options,
    enabled: chatEnabled ? (options?.enabled ?? !!threadId) : false,
  })
}

export function useFetchChatThreads(
  params?: ChatThreadParams,
  options?: Omit<UseQueryOptions<ChatThread[], Error>, "queryKey" | "queryFn">,
  bookIdOverride?: string
) {
  const { enabled: chatEnabled } = useProductChatAccess()
  const getContext = useApiContext(bookIdOverride)
  const bookId = useBookId()
  const { userId } = useDevAuth()
  const queryKey =
    params?.chat_threadable_type && params?.chat_threadable_id
      ? ["chat_threads_list", params.chat_threadable_type, params.chat_threadable_id]
      : ["chat_threads_list"]

  return useQuery<ChatThread[], Error>({
    queryKey: params?.chat_threadable_type === "Dashboard" ? [...queryKey, bookIdOverride ?? bookId, userId] : queryKey,
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchChatThreads(context, params)
    },
    ...options,
    enabled: chatEnabled ? (options?.enabled ?? true) : false,
  })
}
